import { z } from 'zod';

/**
 * Game-agnostic provably-fair bootstrap. Seeds live per (operator, player) in
 * dice_seed_states; these contracts only cover the short-lived handoff that lets
 * CasAPI (or a client) read the commitment before any game launch.
 */

export const FairnessScheme = z.enum([
  'dice-p2g-hmac-v1',
  'crash-drand-hmac-v1',
  'blackjack-p2g-hmac-v1',
  'plinko-p2g-hmac-v1',
  'space-p2g-hmac-v1',
  'summit-p2g-hmac-v1',
  'mines-p2g-hmac-v1',
  'keno-p2g-hmac-v1',
]);
export type FairnessScheme = z.infer<typeof FairnessScheme>;

export const FairnessCommitmentView = z.object({
  scheme: FairnessScheme,
  serverSeedHash: z.string().regex(/^[0-9a-f]{64}$/),
  clientSeed: z.string(),
  nonce: z.string().regex(/^(?:0|[1-9]\d*)$/),
});
export type FairnessCommitmentView = z.infer<typeof FairnessCommitmentView>;

export const FairnessBootstrapRequest = z.object({
  playerId: z.string().min(1).max(128),
  playerName: z.string().max(64).optional(),
  playerIp: z.string().max(64).optional(),
  /** Applied only when creating the player's first seed row. */
  clientSeed: z.string().min(1).max(128).optional(),
  sessionTimeoutSeconds: z.number().int().min(60).max(86400).optional(),
});
export type FairnessBootstrapRequest = z.infer<typeof FairnessBootstrapRequest>;

export const FairnessBootstrapResponse = z.object({
  status: z.literal('OK'),
  bootstrapId: z.uuid(),
  /** Single-use, short-lived. Exchanged for a fairness session token. */
  launchToken: z.string(),
  expiresAt: z.iso.datetime(),
});
export type FairnessBootstrapResponse = z.infer<typeof FairnessBootstrapResponse>;

export const FairnessSessionInitRequest = z.object({
  launchToken: z.string().min(16),
});
export type FairnessSessionInitRequest = z.infer<typeof FairnessSessionInitRequest>;

export const FairnessSessionInitResponse = z.object({
  status: z.literal('OK'),
  fairnessSessionToken: z.string(),
  bootstrapId: z.uuid(),
  expiresAt: z.iso.datetime(),
  player: z.object({
    playerId: z.string(),
    displayName: z.string().nullable(),
  }),
  commitment: FairnessCommitmentView,
});
export type FairnessSessionInitResponse = z.infer<typeof FairnessSessionInitResponse>;

export const FairnessCommitmentResponse = z.object({
  status: z.literal('OK'),
  scheme: FairnessScheme,
  serverSeedHash: z.string().regex(/^[0-9a-f]{64}$/),
  clientSeed: z.string(),
  nonce: z.string().regex(/^(?:0|[1-9]\d*)$/),
});
export type FairnessCommitmentResponse = z.infer<typeof FairnessCommitmentResponse>;

export const FairnessSetClientSeedRequest = z.object({
  clientSeed: z.string().min(1).max(128),
});
export type FairnessSetClientSeedRequest = z.infer<typeof FairnessSetClientSeedRequest>;
